import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useIntl } from "react-intl";
import type { PassengerDetails } from "src/PassengerDetailsProvider";
import type { SchedulesResponse } from "src/api/SchedulesResponse";
import type { SearchResponse } from "src/api/SearchResponse";
import { useTravelSearchSettings } from "src/domain/SegmentScreen/TravelSearchSettingsProvider";
import { useTripPlannerContext } from "src/domain/TripPlanner/hooks/useTripPlannerContext";
import { localeToLanguageCode } from "src/utils/conversions/languageCode";
import useSearch from "src/utils/hooks/useSearch";
import type { Mode } from "src/utils/types/mode";
import { getUidCookie } from "src/utils/uid";
import { getTransportData } from "../getTransportData";
import { getCarriers, SEARCH_SCHEDULE_REQUEST } from "../utils";
import { sendStackAdaptPixel } from "./sendStackAdaptPixel";

type StackAdaptParams = {
  searchResponse?: SearchResponse;
  schedulesResponse?: SchedulesResponse;
  passengers?: PassengerDetails;
  transitMode?: Mode;
  routeIndex?: number;
};

const useStackAdapt = ({
  searchResponse,
  schedulesResponse,
  passengers,
  transitMode,
  routeIndex,
}: StackAdaptParams) => {
  const { origin, destination } = useSearch();
  const intl = useIntl();
  const uid = getUidCookie();
  const uidCookie = getUidCookie();
  const languageCode = localeToLanguageCode(intl.locale);
  const { returnDate } = useTravelSearchSettings().dateObject;
  const numTripLegs = useTripPlannerContext().tripRoutes.placePairs.length;

  const { childPassengers, adultPassengers } = useMemo(() => {
    let childPassengers = 0,
      adultPassengers = 0;

    for (const passenger of passengers ?? []) {
      if (passenger.type === "youth") {
        childPassengers++;
      } else {
        adultPassengers++;
      }
    }

    return { childPassengers, adultPassengers };
  }, [passengers]);

  const disableQuery =
    !origin || !destination || (transitMode && schedulesResponse === undefined);
  useQuery({
    queryKey: ["useStackAdapt", searchResponse?.request, schedulesResponse],
    queryFn: () => {
      if (origin && destination) {
        const data = getTransportData({
          searchResponse,
          routeIndex,
          schedulesResponse,
          transitMode,
          numTripLegs,
          returnDate,
        });

        sendStackAdaptPixel({
          event: "transportSearch",
          level: SEARCH_SCHEDULE_REQUEST,
          origin: origin?.canonicalName,
          destination: destination?.canonicalName,
          travelMode: transitMode,
          travelCarriers: getCarriers(schedulesResponse) ?? [],
          operator: getCarriers(schedulesResponse)?.join(", "),
          outBoundDate: data.departureDate,
          inBoundDate: data.arrivalDate,
          passengerCount: passengers?.length,
          oneWay: !!returnDate,
          currencyCode: data.currencyType,
          price: data.price,
          destinationAirport: data.arrivalAirportCode,
          departureAirport: data.departureAirportCode,
          languageCode,
          uid,
          uidCookie,
          account: "RTR",
          dataSource: "dp",
          childPassengers,
          adultPassengers,
        });
      }

      // Queries must return a value.
      return "";
    },

    refetchOnWindowFocus: false,
    enabled: !disableQuery,
  });
};

export default useStackAdapt;
